import React, { useEffect, useState, useRef, useCallback } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

const API_BASE = `${API_BASE_URL}/admin/gallery`;

const AdminGallery = () => {
  const navigate = useNavigate();
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [category, setCategory] = useState("rpc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchGalleryItems = useCallback(async (token) => {
    try {
      setLoading(true);
      const res = await fetch(API_BASE, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setGalleryItems(
        Array.isArray(data)
          ? data.reverse().map((item) => ({
              id: item._id,
              url: item.url,
              filename: item.filename,
              category: item.category || "rpc",
            }))
          : []
      );
    } catch {
      console.error("Failed to fetch gallery items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    try {
      const { token } = JSON.parse(adminInfo);
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem("adminInfo");
        return navigate("/admin");
      }
      fetchGalleryItems(token);
    } catch {
      localStorage.removeItem("adminInfo");
      navigate("/admin");
    }
  }, [fetchGalleryItems, navigate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      alert("Only image files are permitted.");
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const uploadGalleryItem = async () => {
    if (!selectedFile) return;
    const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("category", category);
      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Upload failed");
      const newItem = await res.json();
      setGalleryItems([{ id: newItem._id, ...newItem }, ...galleryItems]);
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      alert("✅ Gallery Fragment Deployed.");
    } catch {
      alert("Deployment failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id) => {
    const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
    if (!window.confirm("Purge this media fragment permanently?")) return;
    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      setGalleryItems(galleryItems.filter((item) => item.id !== id));
    } catch {
      alert("Purge failed.");
    }
  };

  const filteredList =
    filterCategory === "all"
      ? galleryItems
      : galleryItems.filter((g) => g.category === filterCategory);

  if (loading && galleryItems.length === 0) return (
    <div className="admin-list-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: '20px' }}>
            <div className="loading-spinner"></div>
            <p style={{ fontWeight: 700, color: '#475569' }}>⏳ Synchronizing Media Catalog Matrix...</p>
        </div>
    </div>
  );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Media Catalog Matrix</h1>
        </div>
        <div className="module-badge" style={{ background: '#0ea5e9' }}>Resources</div>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: '#0ea5e9' }}></div>
            Operator: Visual Asset Intelligence
          </div>
          <div className="admin-controls">
                <select
                    className="elite-select"
                    style={{ width: '220px' }}
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                >
                    <option value="all">All Media Verticals</option>
                    <option value="rpc">RPC Operations</option>
                    <option value="fpv">FPV Systems</option>
                    <option value="mapping">Geospatial Mapping</option>
                </select>
          </div>
          <div className="admin-status-badge" style={{ background: '#f0f9ff', color: '#0369a1', fontSize: '0.75rem', fontWeight: 800 }}>
             {filteredList.length} VERIFIED ASSETS
          </div>
        </div>

        <div className="admin-table-wrapper" style={{ padding: '40px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h3 style={{ color: '#0f172a', fontWeight: '800' }}>Deployed Visual Fragments</h3>
                <div style={{ display: 'flex', gap: '15px' }}>
                     {/* ADD BUTTON or trigger form */}
                </div>
            </div>

            <div className="admin-gallery-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', 
                gap: '24px',
                marginBottom: '50px'
            }}>
                {filteredList.length === 0 ? (
                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '100px', color: '#94a3b8' }}>
                        No media fragments detected in the current filter buffer.
                    </div>
                ) : (
                    filteredList.map((item) => (
                        <div key={item.id} className="admin-data-item" style={{ 
                            flexDirection: 'column', 
                            padding: '12px',
                            position: 'relative'
                        }}>
                            <img
                                src={`${API_BASE_URL}${item.url}`}
                                alt={item.filename}
                                style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '12px', background: '#f1f5f9' }}
                            />
                            <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span className="status-tag status-progress" style={{ width: 'fit-content', fontSize: '0.65rem' }}>
                                    {item.category.toUpperCase()}
                                </span>
                                <button className="action-btn-elite btn-delete" onClick={() => deleteItem(item.id)} title="Purge Fragment">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="admin-upload-zone" style={{ paddingTop: '40px', borderTop: '2px dashed #f1f5f9' }}>
                <h3 style={{ marginBottom: '30px', color: '#0f172a', fontWeight: '800' }}>Initialize New Media Deployment</h3>
                <div className="admin-form-elite" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '30px', padding: '30px', border: 'none', background: '#f8fafc', borderRadius: '20px' }}>
                    <label>
                        Media Vertical
                        <select value={category} onChange={(e) => setCategory(e.target.value)}>
                            <option value="rpc">RPC Operations</option>
                            <option value="fpv">FPV Systems</option>
                            <option value="mapping">Geospatial Mapping</option>
                        </select>
                    </label>

                    <label>
                        Asset Logic
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} />
                    </label>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                         {selectedFile && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <img src={previewUrl} alt="preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px', border: '2px solid #6366f1' }} />
                                <button className="btn-primary-elite" onClick={uploadGalleryItem} disabled={loading} style={{ padding: '15px 30px' }}>
                                    {loading ? "INITIALIZING..." : "DEPLOY TO CATALOG"}
                                </button>
                            </div>
                         )}
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AdminGallery;
